import { DataTable, EmptyState, Icon, Panel } from '@dayon/design-system';
import { formatCount } from '@dayon/money';
import { attemptColumns } from './columns.tsx';
import type { AttemptRow } from './types.ts';

/**
 * Tab 3 — the sign-in trail, and where the audit export lives.
 *
 * These are two different logs and the tab says which is which. What is drawn
 * is identity.auth_attempt: who tried to sign in, which factor they reached and
 * whether it worked. The design's Time / Actor / Action / Module table is
 * platform.audit_log, a different table entirely — append-only, hash-chained,
 * and the thing "Export audit log" exports.
 *
 * ── WHY NEITHER TABLE IS NAMED ON THE SCREEN ───────────────────────────────
 *
 * Both notices used to print both names, and the distinction those notices draw
 * is the entire reason this tab carries a notice at all: an investigator handed
 * the wrong log is reading failed passwords where they expected role changes.
 * The names themselves, though, do no work for the person reading them. A
 * security lead cannot look `identity.auth_attempt` up, cannot ask anybody for
 * it and cannot quote it to a regulator. What they can use is the DIFFERENCE
 * between the two logs — who tried to get in, versus what an administrator did
 * once they were in — so that is what the prose says now, and the identifiers
 * stay here in the comment, where whoever next edits this file will find them.
 *
 * The same reasoning covers the other identifiers that used to sit on screen.
 * `GET /api/access/attempts` is the read behind the table below, and
 * `platform.verify_audit_chain()` is the function that walks the hash chain for
 * the Audit trail integrity panel on the system administration screen. Both are
 * things only a developer can act on, so both now appear only in this comment.
 *
 * ── THE TWO EMPTY STATES ARE OPPOSITE FAULTS ───────────────────────────────
 *
 * `failed` means the read never answered. The other branch means it answered
 * and had nothing to give. They draw the same blank table and they mean
 * opposite things, so each one says which of the two happened and neither is
 * allowed to imply the other. A console that says "nobody has tried to sign in"
 * while the read is in fact broken is reassuring a security lead that no one is
 * knocking at precisely the moment they most need to know that somebody is.
 *
 * ── WHAT THE DESIGN PROMISES THAT THE SCHEMA DOES NOT KEEP ─────────────────
 *
 * platform.audit_log has no module or surface column at all, so the design's
 * "Player funds" / "Risk" cell has nothing behind it, and the closing notice
 * says so rather than inventing a value for it. Retention is the same shape of
 * gap: no table carries a retention_until or legal-hold column, and there is no
 * partitioning plan for a seven-year append-only chain, so "Retained seven
 * years" is an intention and the notice reports it as one. Both sentences are
 * written in terms of what is MISSING from the record rather than which column
 * is absent, because the operator's question is whether they can rely on the
 * promise, not how the storage is laid out.
 */
export function SignInTrail({
  attempts,
  loading,
  failed,
}: {
  attempts: AttemptRow[];
  loading: boolean;
  failed: boolean;
}) {
  return (
    <>
      <div className="notice notice-quiet">
        <Icon name="warning-circle" />
        <div>
          This is the <strong>sign-in trail</strong> — who tried to get into the back office, how
          far each attempt reached, and whether it worked. It is not the record of what
          administrators did once they were in. A Time / Actor / Action / Module list of that is
          the other log, which is kept separately: it can only ever be added to, and each
          entry carries its own fingerprint plus the previous entry&rsquo;s, so anybody reading it
          can satisfy themselves that nothing was removed or altered. It is shown on the system
          administration screen, which also walks that chain end to end and reports any break, and
          per administrator under RECENT ACTIONS in the drawer; it is what{' '}
          <strong>Export audit log</strong> above extracts. That log does not record which part of
          the platform an action touched, so a Module entry such as &ldquo;Player funds&rdquo; /
          &ldquo;Risk&rdquo; still has nothing behind it.
        </div>
      </div>

      <Panel title="Sign-in attempts · newest first">
        {loading && attempts.length === 0 ? (
          <div className="skeleton" style={{ height: '240px' }}>
            &nbsp;
          </div>
        ) : (
          <DataTable
            caption="Admin sign-in attempts, newest first, with the factor each one reached"
            columns={attemptColumns}
            rows={attempts}
            rowKey={(a) => `${a.created_at}-${a.email_attempted}-${a.stage}`}
            empty={
              failed ? (
                <EmptyState icon="warning-circle" title="The trail could not be read">
                  The platform did not answer when this screen asked it for the sign-in attempts, so
                  not one attempt was read. Nothing here says the trail is empty and nothing here
                  says nobody has tried to sign in — a hostile run against this console would look
                  exactly like this screen does now. Try again shortly.
                </EmptyState>
              ) : (
                <EmptyState icon="clock-counter-clockwise" title="No sign-in attempts recorded">
                  The platform answered, and it holds no sign-in attempt at all. Since you are
                  reading this over a signed-in session — which is itself an attempt that worked —
                  an empty trail is a fault in the recording worth raising, not a quiet period.
                </EmptyState>
              )
            }
          />
        )}
        <div className="pagination">
          {failed
            ? 'No count: the read did not answer, so this is not "0 attempts".'
            : attempts.length >= 100
              ? 'Showing the 100 most recent attempts. The read stops at 100 and this screen has no way to ask for the ones before them, so older attempts are not reachable from here.'
              : `Showing all ${formatCount(attempts.length)} attempts that came back; the read stops at 100 and this screen cannot ask for the ones before them.`}
        </div>
      </Panel>

      <div className="notice notice-quiet">
        <Icon name="info" />
        <div>
          The plan is &ldquo;Retained seven years · exportable to CSV by any role holding
          audit.export&rdquo;. The export half is real: <strong>Export audit log</strong> above runs
          that privileged-action log through the same machinery as every other report — an account
          that can only read and never change anything, the same ceiling on how many rows one file
          may hold, the same time limit on the read, and the same guard that stops a spreadsheet
          treating a cell of exported text as a formula — and it records the export itself in the
          log it has just exported. The RETENTION half is still only an intention: nothing yet
          records when an entry may be deleted or marks one as held for a case, and nothing has been
          planned for keeping seven years of a log that can only grow.
        </div>
      </div>
    </>
  );
}
